import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../../css/result-spinner.css';

const easeOutQuad = t => t * (2 - t);

const ResultSpinner = ({
  entries = [],
  winner,         // Object with at least a "username" property
  onComplete,     // Callback called after the spinner stops
  visibleCount = 7,
  defaultItemWidth = 100,  // fallback width of each slot
  duration = 14750,        // spin duration in ms
  numRotations = 5,        // desired number of full rotations
  startPause = 2000,
  finalPause = 2000,
  soundUrl = '/sound/tick.wav'  // tick sound file URL
}) => {
  // Refs to measure container and an item
  const containerRef = useRef(null);
  const itemRef = useRef(null);

  // State to hold measured dimensions
  const [containerWidth, setContainerWidth] = useState(visibleCount * defaultItemWidth);
  const [measuredItemWidth, setMeasuredItemWidth] = useState(defaultItemWidth);

  // Measure container and effective item width on mount
  useEffect(() => {
    if (containerRef.current) {
      const cw = containerRef.current.getBoundingClientRect().width;
      setContainerWidth(cw);
    }
    if (itemRef.current) {
      const iw = itemRef.current.getBoundingClientRect().width;
      const computedStyle = window.getComputedStyle(itemRef.current);
      const marginRight = parseFloat(computedStyle.marginRight) || 0;
      const effectiveWidth = iw + marginRight;
      setMeasuredItemWidth(effectiveWidth);
    }
  }, []);

  // 1. Prepare the base entries; ensure the winner is included.
  const baseEntries = useMemo(() => {
    let valid = entries.filter(entry => entry && entry.username);
    const normalizedWinner = winner && winner.username ? winner.username.trim().toLowerCase() : '';
    const winnerIncluded = valid.some(
      entry => entry.username.trim().toLowerCase() === normalizedWinner
    );
    if (!winnerIncluded && winner && winner.username) {
      valid.push(winner);
    }
    if (valid.length < visibleCount) {
      const copies = Math.ceil(visibleCount / valid.length);
      return Array(copies).fill(valid).flat();
    }
    return valid;
  }, [entries, visibleCount, winner]);

  // 2. Shuffle the base entries.
  const shuffledBase = useMemo(() => {
    const copy = [...baseEntries];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [baseEntries]);

  // 3. Compute duplicateCount based on numRotations so that we always have enough items.
  const extraDuplicateMargin = 10;
  const computedDuplicateCount = Math.max(10, 2 * numRotations + extraDuplicateMargin);

  // Duplicate the shuffled base entries.
  const spinnerEntries = useMemo(() => {
    const duplicated = Array(computedDuplicateCount).fill(shuffledBase).flat();
    return duplicated;
  }, [shuffledBase, computedDuplicateCount]);

  // 4. Compute the container’s center.
  const centerPos = containerWidth / 2;

  // 5. Find the winner’s index in the shuffled base.
  const normalizedWinner = winner && winner.username ? winner.username.trim().toLowerCase() : '';
  const winnerIndexInBase = useMemo(() => {
    const idx = shuffledBase.findIndex(
      entry => entry.username.trim().toLowerCase() === normalizedWinner
    );
    return idx;
  }, [shuffledBase, normalizedWinner]);
  const targetIndexInBase = winnerIndexInBase !== -1 ? winnerIndexInBase : Math.floor(shuffledBase.length / 2);

  // 6. Choose a target index in the duplicated array.
  const baseLength = shuffledBase.length;
  const baseCopyIndex = Math.floor(computedDuplicateCount / 2);
  const finalTargetIndex = (baseCopyIndex + numRotations) * baseLength + targetIndexInBase;

  // 7. Calculate the final offset.
  const finalOffset = useMemo(() => {
    const randomPoint = 5 + Math.random() * (measuredItemWidth - 10);
    const targetItemPosition = finalTargetIndex * measuredItemWidth + randomPoint;
    const offset = centerPos - targetItemPosition;
    return offset;
  }, [centerPos, finalTargetIndex, measuredItemWidth]);

  // 8. Animate the spinner.
  const [offsetX, setOffsetX] = useState(0);
  const startTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeoutRef = useRef(null);
  const audioRef = useRef(null);
  const lastPegIndexRef = useRef(null);

  useEffect(() => {
    // Setup the tick sound audio element
    if (soundUrl) {
      audioRef.current = new Audio(soundUrl);
      // Ensure we don't loop the tick sound
      audioRef.current.loop = false;
    }
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
        // Initialize the last peg index based on the starting offset (0)
        const initialPeg = Math.round((containerWidth / 2 - 0 - measuredItemWidth / 2) / measuredItemWidth);
        lastPegIndexRef.current = initialPeg;
      }
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuad(progress);
      const currentOffset = finalOffset * easedProgress;
      setOffsetX(currentOffset);

      // Calculate which entry's center is aligned with the container center.
      // Each item's center is at: (index * measuredItemWidth + measuredItemWidth/2 + currentOffset)
      // We solve for index when that equals containerWidth/2.
      const currentPegIndex = Math.round(
        (containerWidth / 2 - currentOffset - measuredItemWidth / 2) / measuredItemWidth
      );

      // When a new peg passes the center, clone and play the tick sound.
      if (currentPegIndex > lastPegIndexRef.current) {
        lastPegIndexRef.current = currentPegIndex;
        if (audioRef.current) {
          const tickSound = audioRef.current.cloneNode();
          tickSound.play().catch(err => {
            console.error('Tick sound playback failed:', err);
          });
        }
      }

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          onComplete && onComplete();
        }, finalPause);
      }
    };

    // Start spinner after startPause
    timeoutRef.current = setTimeout(() => {
      startTimeRef.current = null;
      animationFrameRef.current = requestAnimationFrame(animate);
    }, startPause);

    return () => {
      clearTimeout(timeoutRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [finalOffset, duration, finalPause, onComplete, startPause, containerWidth, measuredItemWidth, soundUrl]);

  return (
    <div className="result-spinner-container" ref={containerRef}>
      <div
        className="result-spinner-track"
        style={{ transform: `translateX(${offsetX}px)` }}
      >
        {spinnerEntries.map((entry, idx) => (
          <div
            className="result-spinner-item"
            key={idx}
            ref={idx === 0 ? itemRef : null}
          >
            <img src={entry.avatar} alt={entry.username} className="spinner-avatar" />
            <div className="spinner-username">{entry.username}</div>
          </div>
        ))}
      </div>
      {/* The center stopper indicates the winning slot */}
      <div className="spinner-stopper"></div>
    </div>
  );
};

export default ResultSpinner;
